import Page from "../Page";
import QRCode from "./QRCode";
import Api from "../Api";
import HttpClient from "../HttpClient";
import EditQRCodePayload from "./EditQRCodePayload";
import GetListQuery from "./GetListQuery";
import CreatePayload from "./CreatePayload";
import SimpleQRCode from "./SimpleQRCode";

// noinspection JSUnusedGlobalSymbols
/**
 * Api to work with QR codes.
 * It's using /api/qr-code/ endpoint basically.
 * @author Kacper Faber
 * @since 0.1.3
 */
class QRCodeApi {
    /**
     * GET /api/qr-code/
     * @author Kacper Faber
     * @since 0.1.3
     * @param query GetListQuery
     */
    static getList(query: GetListQuery): Promise<Page<SimpleQRCode>> {
        const url = Api.getUrl('/api/qr-code/');
        return HttpClient.get<Page<QRCode>>(url, { ...query });
    }

    /**
     * Creates new QRCode.
     * @author Kacper Faber
     * @since 0.1.6
     * @param payload
     */
    static create(payload: CreatePayload): Promise<QRCode> {
        const url = Api.getUrl('/api/qr-code/');
        return HttpClient.postFormData<QRCode>(url, {}, { ...payload });
    }

    /**
     * Delete QRCode by id.
     * Sends an HTTP request to API and waits for an answer.
     * You use then/catch to check if request processed correctly.
     * @example
     * QRCodeApi.deleteById(id)
     * .then() // success
     * .catch() // failed
     * @param id
     * @since 0.1.3
     */
    static deleteById(id: number | string): Promise<void> {
        const url = Api.getUrl(`/api/qr-code/${id}/`);
        return HttpClient.execNoContent(url, "DELETE", {}, {});
    }

    /**
     * Delete QRCode by id retrieved from QRCode.
     * Sends an HTTP request to API and waits for an answer.
     * You use then/catch to check if request processed correctly.
     * @example
     * QRCodeApi.deleteById(qrCodeWithValidId)
     * .then() // success
     * .catch() // failed
     * @param qrCode QRCode instance with valid ID.
     * @since 0.1.3
     */
    static delete(qrCode: QRCode): Promise<void> {
        const url = Api.getUrl(`/api/qr-code/${qrCode.id}/`);
        return HttpClient.execNoContent(url, "DELETE", {}, {});
    }

    /**
     * Retrieve QRCode by ID
     * @param id
     * @author Kacper Faber
     * @since 0.1.4
     */
    static getById(id: string | number): Promise<QRCode> {
        const url = Api.getUrl(`/api/qr-code/${id}/`);
        return HttpClient.get<QRCode>(url, {});
    }

    /**
     * Updates QRCode with id.
     * @param id
     * @param payload
     * @author Kacper Faber
     * @since 0.1.4
     */
    static editById(id: number, payload: EditQRCodePayload): Promise<QRCode> {
        const url = Api.getUrl(`/api/qr-code/${id}/`);
        return HttpClient.putFormData(url, {}, { ...payload });
    }

    static downloadPdf(id: number): Promise<Response> {
        const url = Api.getUrl(`/api/qr-code/${id}/download-pdf/`);
        return HttpClient.getResponse(url, {});
    }

    static downloadSvg(id: number): Promise<Response> {
        const url = Api.getUrl(`/api/qr-code/${id}/download-svg/`);
        return HttpClient.getResponse(url, {});
    }
}

// noinspection JSUnusedGlobalSymbols
export default QRCodeApi;